import { useEffect } from "react"

import { useNavigate } from "react-router-dom"

import {
  isEditableEscapeTarget,
  proposalSetupQuickSearchCommand,
  type ProposalSetupEscapeCommand,
  type ProposalSetupEscapeEvent,
} from "./proposal-setup-shortcuts"

/**
 * Escape and ⌘K / Ctrl+K for a proposal's Setup rail. Quick search answers
 * first, so Escape with the palette open closes only the palette.
 */
export const useProposalSetupShortcuts = ({
  enabled,
  escapeCommand,
  searchOpen,
  setSearchOpen,
}: {
  readonly enabled: boolean
  readonly escapeCommand: (
    event: ProposalSetupEscapeEvent,
  ) => ProposalSetupEscapeCommand
  readonly searchOpen: boolean
  readonly setSearchOpen: (open: boolean) => void
}): void => {
  const navigate = useNavigate()

  useEffect(() => {
    if (!enabled) {
      return
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      const searchCommand = proposalSetupQuickSearchCommand({
        altKey: event.altKey,
        ctrlKey: event.ctrlKey,
        defaultPrevented: event.defaultPrevented,
        isComposing: event.isComposing,
        key: event.key,
        metaKey: event.metaKey,
        open: searchOpen,
        repeat: event.repeat,
        shiftKey: event.shiftKey,
      })

      switch (searchCommand.kind) {
        case `open`:
          event.preventDefault()
          setSearchOpen(true)
          return
        case `close`:
          event.preventDefault()
          event.stopImmediatePropagation()
          setSearchOpen(false)
          return
        case `ignore`:
          break
        default: {
          const _exhaustive: never = searchCommand
          return _exhaustive
        }
      }

      if (
        event.key !== `Escape` ||
        event.isComposing ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
      ) {
        return
      }

      const command = escapeCommand({
        defaultPrevented: event.defaultPrevented,
        repeat: event.repeat,
        targetIsEditable: isEditableEscapeTarget(event.target),
      })

      switch (command.kind) {
        case `ignore`:
          return
        case `open`:
        case `close`:
          event.preventDefault()
          navigate(command.to)
          return
        default: {
          const _exhaustive: never = command
          return _exhaustive
        }
      }
    }

    // Capture runs before a step drawer's own Escape listener, so closing
    // search does not also dismiss the drawer underneath.
    document.addEventListener(`keydown`, onKeyDown, true)
    return () => {
      document.removeEventListener(`keydown`, onKeyDown, true)
    }
  }, [enabled, escapeCommand, navigate, searchOpen, setSearchOpen])
}
